import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Package } from 'lucide-react';
import type { ProductoServicio } from '@/lib/types';
import { formatEUR } from '@/lib/calc';

interface Props {
  open: boolean;
  onClose: () => void;
  productos: ProductoServicio[];
  onSelect: (p: ProductoServicio) => void;
}

export function CatalogoSelector({ open, onClose, productos, onSelect }: Props) {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('Todos');

  const activos = productos.filter(p => p.activo);
  const categorias = ['Todos', ...Array.from(new Set(activos.map(p => p.categoria).filter(Boolean)))];

  const filtrados = activos.filter(p => {
    const query = q.toLowerCase();
    const matchQ = !q || p.nombre.toLowerCase().includes(query) || p.codigo.toLowerCase().includes(query) || p.descripcion.toLowerCase().includes(query);
    const matchCat = cat === 'Todos' || p.categoria === cat;
    return matchQ && matchCat;
  });

  const handleSelect = (p: ProductoServicio) => {
    onSelect(p);
    setQ('');
    setCat('Todos');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={() => { setQ(''); setCat('Todos'); onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <DialogTitle className="text-base flex items-center gap-2">
            <Package size={16} className="text-primary" /> Añadir desde catálogo
          </DialogTitle>
          <div className="relative mt-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Buscar producto o servicio…"
              value={q}
              onChange={e => setQ(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
          {/* Filtro de categoría */}
          {categorias.length > 2 && (
            <div className="flex gap-1.5 flex-wrap mt-2">
              {categorias.map(c => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`px-2.5 py-0.5 rounded-full text-xs border transition-colors ${cat === c ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted border-border hover:border-primary/50'}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </DialogHeader>

        <div className="overflow-y-auto flex-1">
          {filtrados.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center px-4">
              <p className="text-sm text-muted-foreground">
                {q || cat !== 'Todos' ? 'No se encontraron productos' : 'No hay productos activos en el catálogo'}
              </p>
            </div>
          ) : (
            filtrados.map(p => (
              <button
                key={p.id}
                onClick={() => handleSelect(p)}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0 text-left group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {p.codigo && <span className="font-mono text-xs text-muted-foreground">{p.codigo}</span>}
                    {p.categoria && <Badge variant="outline" className="text-xs font-normal py-0">{p.categoria}</Badge>}
                  </div>
                  <p className="font-medium text-sm truncate">{p.nombre}</p>
                  {p.descripcion && <p className="text-xs text-muted-foreground truncate">{p.descripcion}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono font-bold text-sm text-foreground">{formatEUR(p.precioBase)}</p>
                  <p className="text-xs text-muted-foreground">/{p.unidad} · IVA {p.iva}%</p>
                  {p.descuento > 0 && <p className="text-xs text-emerald-600">−{p.descuento}% dto.</p>}
                </div>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
